import React, { useMemo, useState } from 'react';

import { Formik } from 'formik';
import merge from 'lodash/merge';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';

import { showSmsConsent } from '~/acquisition/utils/smsConsent';
import { useSMSOptInCookie } from '~/acquisition/utils/useSMSOptInCookie';
import { HowDidYouHearDropdown } from '~/builderQuiz/components/HowDidYouHearDropdown';
import { getOptOutStatus } from '~/builderQuiz/utils/builderQuiz';
import { trackCompleteRegistration } from '~/shared/actions';
import { CountryCodes } from '~/shared/constants/countryCode';
import featureNames from '~/shared/constants/featureFlags';
import { formatUSPhoneNumber } from '~/shared/utils/formatUSPhoneNumber';
import { isLocale } from '~/shared/utils/helpers';
import logger from '~/shared/utils/logger';
import useGender from '~/shared/utils/useGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useAccountActions } from '~/techstyle-shared/react-accounts';
import { Button, Checkbox, mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import {
  useMarketingActions,
  useReferrerOptions,
} from '~/techstyle-shared/react-marketing';
import { useDomain } from '~/techstyle-shared/redux-core';

import { addContact } from './addContact';
import { errorMessages, getSignupError, validateForm } from './formValidation';
import SignupTextField from './SignupTextField';

const DynamicFormattedMessage = FormattedMessage;

const fields = defineMessages({
  hdyh: {
    id: 'signup.hdyh_label',
    defaultMessage: 'How did you hear about us? *',
  },
  sms: {
    id: 'signup.sms_label',
    defaultMessage: 'Phone Number (optional)',
  },
});

const debug = logger.extend('SignupForm');

const CheckboxContainer = styled.div`
  display: flex;

  & > label {
    ${({ theme: { typography } }) => typography.variants.termsText.textStyle}
    align-self: center;
    line-height: 1;
    text-align: left;

    ${({ theme, hasError }) =>
      hasError &&
      `
      color: ${theme.colors.error};
      font-weight: 800;
    `}
  }

  &&& > div {
    margin-right: 8px;

    ${({ theme, hasError }) =>
      hasError &&
      `
      > span {
        border-color: ${theme.colors.error};
      }
    `}
  }
`;

const Form = styled.form`
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 410px;
  margin-inline: auto;

  // scope some css vars for re-use
  --input-error-color: ${({ theme }) => theme.colors.error};
  --input-border-color: ${({ theme }) => theme.colors.borderLight};
  --input-border-width: 1px;
  --input-border-radius: 12px;
  --input-padding: 16px;
`;

const InputWrapper = styled.div`
  position: relative;
  z-index: 2;
  &:not(:first-child) {
    margin-top: -1px;
  }

  // allows any type of input that has
  // the focus or error state to float
  // above the others, allowing for its own border
  // to not be hidden by the other inputs,
  // since they overlap by 1px
  &:has(:focus) {
    z-index: 3;
  }
  &:has(:user-invalid) {
    z-index: 4;
  }
  ${({ $invalidInputOverride }) =>
    $invalidInputOverride &&
    css`
      z-index: 4;
    `}
`;

const Fields = styled.div``;

const EmailField = styled(SignupTextField)``;

const PasswordField = styled(EmailField)``;

const PhoneField = styled(SignupTextField)``;

const HowDidYouHearDropdownWrapper = styled.div`
  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: var(--input-error-color);
      border-bottom: var(--input-border-width) solid var(--input-error-color);
    `}

  button {
    border: var(--input-border-width) solid var(--input-border-color);
    padding: var(--input-padding);
    background: ${({ theme }) => theme.colors.white};

    ${({ $isLastField }) =>
      $isLastField
        ? css`
            border-radius: 0 0 var(--input-border-radius)
              var(--input-border-radius);
          `
        : css`
            border-radius: 0;
          `}

    &[aria-expanded='true'] {
      border-bottom: 0;
      border-radius: 0;
    }
  }
  ul {
    max-height: 150px;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.palette.white};
  background-color: var(--input-error-color);
  font-size: 14px;
  line-height: 1;
  letter-spacing: -0.02em;
  position: relative;
  z-index: 1;
  margin-top: -24px;
  padding: 33px 9px 9px;
  border-radius: var(--input-border-radius);
  border: var(--input-border-width) solid var(--input-error-color);

  a {
    text-decoration: underline;
  }
`;

const SMSDisclosure = styled.div`
  color: ${({ theme, signupSource }) =>
    signupSource === 'pop_up_reg'
      ? theme.colors.promoPickerSignupFormSmsDisclosure
      : theme.colors.signupFormSmsDisclosure};
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
  padding: 12px 0px;

  a {
    text-decoration: underline;
  }

  ${mobile`
    font-size: 10px;
    line-height: ${12 / 10};
    padding-top: 16px;
    padding-bottom: 0px;
  `}
`;

const SubmitButton = styled(Button)`
  margin-top: 16px;
  span {
    ${({ theme }) => theme.promoPickerPopup.signupSubmitButtonText}
  }
`;

const LoadingSpinner = styled.span`
  width: 22px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: conic-gradient(
    from 180deg at 50% 50%,
    white 0deg,
    rgba(196, 196, 196, 0) 360deg
  );
  mask: radial-gradient(6px at 11px 11px, #0000 98%, #000);
  animation: ${keyframes`
    to {
      transform: rotate(1turn);
    }
  `} 1s infinite linear;
`;

function SignupForm({
  onSignupSuccess,
  onSignupError,
  signupData = {},
  signupSource = '',
  showPassword: showPasswordProp,
  buttonLabel = (
    <FormattedMessage
      id="signup.submit_button_label"
      defaultMessage="Claim Your Offer"
    />
  ),
  ...rest
} = {}) {
  const accountActions = useAccountActions();
  const dispatch = useDispatch();
  const intl = useIntl();
  const router = useRouter();
  const { enabled: isPasswordlessSignupEnabled } = useHasFeature(
    featureNames.PASSWORDLESS_SIGNUP
  );

  const options = useReferrerOptions();
  const dropdownOptions = useMemo(() => {
    const defaultOption = {
      value: '',
      label: intl.formatMessage({
        id: 'site_signup_register.choose_opt',
        defaultMessage: 'Choose An Option',
      }),
    };
    return [
      defaultOption,
      ...options.map((option) => ({
        value: option.referrerId.toString(),
        label: option.label,
      })),
    ];
  }, [intl, options]);

  const isPppSignup = signupSource === 'pop_up_reg';

  const [signupError, setSignupError] = useState(null);
  const { gender } = useGender();
  const { enabled: isSMSEnabled } = useHasFeature(
    featureNames.PROMO_PICKER_SMS
  );
  const { tld } = useDomain();
  const marketingActions = useMarketingActions();
  const isUSDomain = isLocale([CountryCodes.US], tld);
  const { setSMSOptInCookie } = useSMSOptInCookie();

  const showPassword =
    showPasswordProp === undefined
      ? !isPasswordlessSignupEnabled
      : showPasswordProp;

  const handleFormSubmit = async (values, formik) => {
    setSignupError(null);
    const { email, password, referrer, phone } = values;
    const { setSubmitting } = formik;

    if (
      showSmsConsent(tld) &&
      isPppSignup &&
      values.phone &&
      !values.smsConsent
    ) {
      return formik.setErrors({
        smsConsent: errorMessages.required,
      });
    }

    try {
      const externalData =
        typeof signupData === 'function'
          ? signupData({ values, formik })
          : signupData;

      const credentials = {
        email,
        password,
      };

      const details = {
        signup_source: signupSource,
        customer_referrer_id: referrer?.value,
      };

      const payload = merge(externalData, { details }, credentials);

      /**
       * SXF EU request
       * If you don't click the consent then you should not be enrolled in any marketing
       */
      if (showSmsConsent(tld)) {
        payload.optOutStatus = getOptOutStatus(tld, values.smsConsent);
      }

      const session = await accountActions.signUp(payload);
      debug('Signup Success', { payload, session });
      if (signupSource) {
        await dispatch(trackCompleteRegistration({ source: signupSource }));
      }

      await addContact(marketingActions.addContact, {
        showSmsConsent: showSmsConsent(tld),
        phone,
        email,
        gender,
        smsConsent: values.smsConsent,
        signupSource,
      }).then((result) => {
        if (phone) {
          debug('phone', phone);
        }

        if (result) {
          debug('contactResult', result);
        }
      });
      // Set the SMS opt-in cookie to true or false if the user given the
      // opportunity to provide a phone number
      if (isSMSEnabled) {
        setSMSOptInCookie(!!phone);
      }

      onSignupSuccess && onSignupSuccess({ payload, session });
      router.replace('/');
      setSubmitting(false);
    } catch (error) {
      const {
        action: {
          meta: { responseBody: response },
        },
      } = error;
      const errorMessage = getSignupError(response);
      setSignupError(errorMessage);
      onSignupError && onSignupError({ error, errorMessage, values, formik });
      debug('Signup Error', { errorMessage, error, values, formik });
    }
  };

  const initialValues = useMemo(
    () => ({
      email: '',
      password: '',
      referrer: { value: '', label: '' },
      phone: '',
    }),
    []
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validate={(values) =>
        validateForm({
          values,
          requirePassword: showPassword,
          tld,
        })
      }
    >
      {({
        values,
        errors,
        setErrors,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
        submitCount,
      }) => {
        const hasSubmit = submitCount > 0;
        return (
          <Form onSubmit={handleSubmit} noValidate {...rest}>
            <Fields>
              <InputWrapper>
                <EmailField
                  aria-required
                  name="email"
                  type="email"
                  aria-label="email"
                  data-is-first-input
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  error={
                    hasSubmit &&
                    errors.email &&
                    intl.formatMessage(errors.email)
                  }
                  required
                  label={
                    <FormattedMessage
                      id="signup.email_label"
                      defaultMessage="Email *"
                    />
                  }
                  data-autotag="email"
                  data-pii="pii-email"
                />
              </InputWrapper>
              {showPassword && (
                <InputWrapper>
                  <PasswordField
                    aria-required
                    name="password"
                    type="password"
                    aria-label="password"
                    onChange={handleChange}
                    value={values.password}
                    error={
                      hasSubmit &&
                      errors.password &&
                      intl.formatMessage(errors.password)
                    }
                    required
                    label={
                      <FormattedMessage
                        id="signup.password_label"
                        defaultMessage="Password (6 characters minimum) *"
                      />
                    }
                    data-autotag="password"
                  />
                </InputWrapper>
              )}
              <InputWrapper
                $invalidInputOverride={!!errors.referrer && hasSubmit}
              >
                <HowDidYouHearDropdownWrapper
                  $hasError={!!errors.referrer && hasSubmit}
                  $isLastField={!isSMSEnabled}
                >
                  <HowDidYouHearDropdown
                    errors={errors}
                    values={values}
                    options={dropdownOptions}
                    setErrors={setErrors}
                    setFieldValue={setFieldValue}
                    handleFormUpdate={() => {}}
                    src="SignupForm"
                    hasError={!!errors.referrer && hasSubmit}
                  />
                </HowDidYouHearDropdownWrapper>
              </InputWrapper>
              {isSMSEnabled && (
                <InputWrapper>
                  <PhoneField
                    aria-required="false"
                    name="phone"
                    aria-label="phone"
                    autoComplete="tel"
                    type="tel"
                    data-is-last-input
                    onChange={(e) => {
                      const phoneInput = e.target.value;

                      // lets restrict the characters
                      // we only want -, or digits
                      // allowed US
                      if (
                        !/^[\d-]*$/.test(phoneInput) &&
                        !showSmsConsent(tld)
                      ) {
                        return;
                      }
                      // allowed EU
                      if (!/^[\d]*$/.test(phoneInput) && showSmsConsent(tld)) {
                        return;
                      }

                      if (isUSDomain) {
                        const formattedPhone = formatUSPhoneNumber(
                          phoneInput.replace(/\D/g, '').slice(0, 10)
                        );
                        return handleChange({
                          target: {
                            name: 'phone',
                            value: formattedPhone,
                          },
                        });
                      }

                      return handleChange(e);
                    }}
                    onBlur={handleBlur}
                    variant="standard"
                    value={values.phone}
                    label={intl.formatMessage(fields.sms)}
                    error={
                      hasSubmit &&
                      errors.phone &&
                      intl.formatMessage(errors.phone)
                    }
                    data-autotag="phone"
                    data-pii="pii-phone-number"
                  />
                </InputWrapper>
              )}
              {hasSubmit && !isValid && (
                <ErrorMessage data-autotag="SignupForm__ErrorMessage">
                  <FormattedMessage
                    id="signup.error_general"
                    defaultMessage="Please fix errors before continuing"
                  />
                </ErrorMessage>
              )}
              {signupError && (
                <ErrorMessage data-autotag="SignupForm__ErrorMessage">
                  <DynamicFormattedMessage
                    id={signupError.id}
                    defaultMessage={signupError.defaultMessage}
                    values={signupError.values || {}}
                  />
                </ErrorMessage>
              )}
            </Fields>
            {isSMSEnabled && (
              <SMSDisclosure signupSource={signupSource}>
                {showSmsConsent(tld) && isPppSignup ? (
                  <CheckboxContainer hasError={errors.smsConsent}>
                    <Checkbox
                      name="smsConsent"
                      id="smsConsent"
                      checked={values.smsConsent}
                      onChange={() => {
                        setFieldValue('smsConsent', !values.smsConsent);
                      }}
                    />
                    <label htmlFor="smsConsent">
                      <FormattedMessage
                        id="ppp.sms_legal"
                        defaultMessage="By submitting this form, you agree to receive recurring automated promotional and personalized marketing text messages (e.g. cart reminders) from Fabletics at the cell number used when signing up. Consent is not a condition of any purchase. Reply HELP for help and STOP to cancel. Msg frequency varies. Msg & data rates may apply. View Terms & Privacy."
                      />
                    </label>
                  </CheckboxContainer>
                ) : (
                  <FormattedMessage
                    id="ppp.sms_legal"
                    defaultMessage="By submitting this form, you agree to receive recurring automated promotional and personalized marketing text messages (e.g. cart reminders) from Fabletics at the cell number used when signing up. Consent is not a condition of any purchase. Reply HELP for help and STOP to cancel. Msg frequency varies. Msg & data rates may apply. View Terms & Privacy."
                  />
                )}
              </SMSDisclosure>
            )}
            <SubmitButton
              data-autotag="SignupForm__SubmitButton"
              type="submit"
              disabled={isSubmitting || (hasSubmit && !isValid)}
            >
              {isSubmitting ? <LoadingSpinner /> : buttonLabel}
            </SubmitButton>
          </Form>
        );
      }}
    </Formik>
  );
}

SignupForm.propTypes = {
  buttonLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onSignupError: PropTypes.func,
  onSignupSuccess: PropTypes.func,
  showPassword: PropTypes.bool,
  signupData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  signupSource: PropTypes.string,
};

SignupForm.EmailField = EmailField;
SignupForm.PasswordField = PasswordField;
SignupForm.HowDidYouHearDropdown = HowDidYouHearDropdown;
SignupForm.SubmitButton = SubmitButton;

export default SignupForm;
